const xmlLikeContentTypes = ['xml', 'soap'];
const jsonLikeContentTypes = ['json'];

function isXmlLike(contentType: string | undefined, payload: string): boolean {
  const normalizedContentType = contentType?.toLowerCase() ?? '';
  return xmlLikeContentTypes.some((type) => normalizedContentType.includes(type)) || payload.trimStart().startsWith('<');
}

function isJsonLike(contentType: string | undefined, payload: string): boolean {
  const normalizedContentType = contentType?.toLowerCase() ?? '';
  return jsonLikeContentTypes.some((type) => normalizedContentType.includes(type)) || /^[\[{]/.test(payload.trimStart());
}

function isXmlOpeningTag(token: string): boolean {
  return (
    token.startsWith('<') &&
    !token.startsWith('</') &&
    !token.startsWith('<?') &&
    !token.startsWith('<!--') &&
    !token.startsWith('<!') &&
    !token.endsWith('/>')
  );
}

function wrapLongXmlTag(token: string, indentLevel: number): string {
  if (token.length <= 80 || token.startsWith('</') || token.startsWith('<?') || token.startsWith('<!--') || token.startsWith('<!')) {
    return token;
  }

  const match = token.match(/^<([^\s/>]+)([\s\S]*?)(\/?)>$/);
  if (!match) {
    return token;
  }

  const [, tagName, rawAttributes, selfClosing] = match;
  const attributes = rawAttributes.trim().match(/[^\s=]+=(?:"[^"]*"|'[^']*')|[^\s=]+/g) ?? [];
  if (attributes.length === 0) {
    return token;
  }

  const attributeIndent = '  '.repeat(indentLevel + 1);
  const closeIndent = '  '.repeat(indentLevel);
  return [
    `<${tagName}`,
    ...attributes.map((attribute) => `${attributeIndent}${attribute}`),
    `${closeIndent}${selfClosing ? '/>' : '>'}`,
  ].join('\n');
}

function formatXmlPayload(payload: string): string {
  const tokens = payload.trim().replace(/>\s+</g, '><').match(/<[^>]+>|[^<]+/g);
  if (!tokens) {
    return payload;
  }

  let indentLevel = 0;
  const lines: string[] = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const trimmed = tokens[i]!.trim();
    if (!trimmed) {
      continue;
    }

    if (!trimmed.startsWith('<')) {
      lines.push(`${'  '.repeat(indentLevel)}${trimmed}`);
      continue;
    }

    if (trimmed.startsWith('</')) {
      indentLevel = Math.max(indentLevel - 1, 0);
      lines.push(`${'  '.repeat(indentLevel)}${trimmed}`);
      continue;
    }

    if (isXmlOpeningTag(trimmed)) {
      // Collapse simple leaves (<tag>text</tag> or <tag></tag>) onto a single line
      // instead of spreading them across three lines - keeps the tree readable.
      const next = tokens[i + 1]?.trim();
      const afterNext = tokens[i + 2]?.trim();

      if (next?.startsWith('</')) {
        lines.push(`${'  '.repeat(indentLevel)}${trimmed}${next}`);
        i += 1;
        continue;
      }

      if (next !== undefined && next.length > 0 && !next.startsWith('<') && afterNext?.startsWith('</')) {
        lines.push(`${'  '.repeat(indentLevel)}${trimmed}${next}${afterNext}`);
        i += 2;
        continue;
      }

      lines.push(`${'  '.repeat(indentLevel)}${wrapLongXmlTag(trimmed, indentLevel)}`);
      indentLevel += 1;
      continue;
    }

    lines.push(`${'  '.repeat(indentLevel)}${wrapLongXmlTag(trimmed, indentLevel)}`);
  }

  return lines.join('\n');
}

/**
 * Pretty-prints a raw XML or JSON payload for display in a content viewer.
 * Used for both inbound (AS2) and outbound (generated document) payloads,
 * since the backend renders both as compact, single-line strings.
 */
export function formatInboundPayload(payload: string, contentType?: string): string {
  if (isJsonLike(contentType, payload)) {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }

  if (isXmlLike(contentType, payload)) {
    return formatXmlPayload(payload);
  }

  return payload;
}
